package songwritersnotebook.app.utils

import songwritersnotebook.app.data.Song

object ChordUtils {
    val ROOT_NOTES = listOf("C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B")
    val ALL_NOTES = (0..8).flatMap { octave -> ROOT_NOTES.map { note -> "$note$octave" } }

    data class ChordMatch(val root: String, val quality: String, val name: String)

    // Defines the musical intervals for each chord quality
    val CHORD_INTERVALS = mapOf(
        "maj" to listOf(0, 4, 7), "m" to listOf(0, 3, 7), "7" to listOf(0, 4, 7, 10),
        "maj7" to listOf(0, 4, 7, 11), "m7" to listOf(0, 3, 7, 10), "dim" to listOf(0, 3, 6),
        "aug" to listOf(0, 4, 8), "sus2" to listOf(0, 2, 7), "sus4" to listOf(0, 5, 7),
        "7sus4" to listOf(0, 5, 7, 10), "m7b5" to listOf(0, 3, 6, 10), "dim7" to listOf(0, 3, 6, 9),
        "add9" to listOf(0, 4, 7, 14), "m(add9)" to listOf(0, 3, 7, 14),
        "6" to listOf(0, 4, 7, 9), "m6" to listOf(0, 3, 7, 9),
        "9" to listOf(0, 4, 7, 10, 14), "maj9" to listOf(0, 4, 7, 11, 14),
        "m9" to listOf(0, 3, 7, 10, 14),
        "11" to listOf(0, 7, 10, 14, 17), // R, 5, b7, 9, 11 (common voicing)
        "13" to listOf(0, 4, 10, 14, 21) // R, 3, b7, 9, 13 (common voicing)
    )

    // Maps the internal quality key to the display name (or what's appended to the root note)
    val CHORD_QUALITIES = mapOf(
        "maj" to "", "m" to "m", "7" to "7", "maj7" to "maj7", "m7" to "m7",
        "dim" to "dim", "aug" to "aug", "sus2" to "sus2", "sus4" to "sus4",
        "7sus4" to "7sus4", "m7b5" to "m7b5", "dim7" to "dim7", "add9" to "add9",
        "m(add9)" to "m(add9)", "6" to "6", "m6" to "m6", "9" to "9"
        , "maj9" to "maj9", "m9" to "m9", "11" to "11", "13" to "13"
    )

    data class ChordQualityCategory(val name: String, val qualities: List<String>)

    val CHORD_QUALITY_CATEGORIES = listOf(
        ChordQualityCategory(
            name = "Triads",
            qualities = listOf("maj", "m", "dim", "aug")
        ),
        ChordQualityCategory(
            name = "Sevenths",
            qualities = listOf("7", "maj7", "m7", "m7b5", "dim7")
        ),
        ChordQualityCategory(
            name = "Suspended",
            qualities = listOf("sus2", "sus4", "7sus4")
        ),
        ChordQualityCategory(
            name = "Extensions",
            qualities = listOf(
                "6", "m6", "add9", "m(add9)", "9", "maj9", "m9", "11", "13"
            )
        )
    )

    // Pre-defined voicings for common piano chords for better sound
    val PIANO_CHORD_VOICINGS = mapOf(
        // --- Major Triads ---
        "C" to listOf("C4", "E4", "G4"), "Db" to listOf("Db4", "F4", "Ab4"),
        "D" to listOf("D4", "Gb4", "A4"), "Eb" to listOf("Eb4", "G4", "Bb4"),
        "E" to listOf("E4", "Ab4", "B4"), "F" to listOf("F3", "A3", "C4"),
        "Gb" to listOf("Gb3", "Bb3", "Db4"), "G" to listOf("G3", "B3", "D4"),
        "Ab" to listOf("Ab3", "C4", "Eb4"), "A" to listOf("A3", "Bb4", "E4"),
        "Bb" to listOf("Bb3", "D4", "F4"), "B" to listOf("B3", "Eb4", "Gb4"),

        // --- Minor Triads ---
        "Cm" to listOf("C4", "Eb4", "G4"), "Dbm" to listOf("Db4", "E4", "Ab4"),
        "Dm" to listOf("D4", "F4", "A4"), "Ebm" to listOf("Eb4", "Gb4", "Bb4"),
        "Em" to listOf("E4", "G4", "B4"), "Fm" to listOf("F3", "Ab3", "C4"),
        "Gbm" to listOf("Gb3", "A3", "Db4"), "Gm" to listOf("G3", "Bb3", "D4"),
        "Abm" to listOf("Ab3", "B3", "Eb4"), "Am" to listOf("A3", "C4", "E4"),
        "Bbm" to listOf("Bb3", "Db4", "F4"), "Bm" to listOf("B3", "D4", "Gb4"),

        // --- Major 7th ---
        "Cmaj7" to listOf("C4", "E4", "G4", "B4"), "Dbmaj7" to listOf("Db4", "F4", "Ab4", "C5"),
        "Dmaj7" to listOf("D4", "Gb4", "A4", "Db5"), "Ebmaj7" to listOf("Eb4", "G4", "Bb4", "D5"),
        "Emaj7" to listOf("E3", "Ab3", "B3", "Eb4"), "Fmaj7" to listOf("F3", "A3", "C4", "E4"),
        "Gbmaj7" to listOf("Gb3", "Bb3", "Db4", "F4"), "Gmaj7" to listOf("G3", "B3", "D4", "Gb4"),
        "Abmaj7" to listOf("Ab3", "C4", "Eb4", "G4"), "Amaj7" to listOf("A3", "Bb4", "E4", "Ab4"),
        "Bbmaj7" to listOf("Bb3", "D4", "F4", "A4"), "Bmaj7" to listOf("B3", "Eb4", "Gb4", "Bb4"),

        // --- Dominant 7th ---
        "C7" to listOf("C4", "E4", "G4", "Bb4"), "Db7" to listOf("Db4", "F4", "Ab4", "B4"),
        "D7" to listOf("D4", "Gb4", "A4", "C5"), "Eb7" to listOf("Eb4", "G4", "Bb4", "Db5"),
        "E7" to listOf("E3", "Ab3", "B3", "D4"), "F7" to listOf("F3", "A3", "C4", "Eb4"),
        "Gb7" to listOf("Gb3", "Bb3", "Db4", "E4"), "G7" to listOf("G3", "B3", "D4", "F4"),
        "Ab7" to listOf("Ab3", "C4", "Eb4", "Gb4"), "A7" to listOf("A3", "Bb4", "E4", "G4"),
        "Bb7" to listOf("Bb3", "D4", "F4", "Ab4"), "B7" to listOf("B3", "Eb4", "Gb4", "A4"),

        // --- Minor 7th ---
        "Cm7" to listOf("C4", "Eb4", "G4", "Bb4"), "Dbm7" to listOf("Db4", "E4", "Ab4", "B4"),
        "Dm7" to listOf("D4", "F4", "A4", "C5"), "Ebm7" to listOf("Eb4", "Gb4", "Bb4", "Db5"),
        "Em7" to listOf("E3", "G3", "B3", "D4"), "Fm7" to listOf("F3", "Ab3", "C4", "Eb4"),
        "Gbm7" to listOf("Gb3", "A3", "Db4", "E4"), "Gm7" to listOf("G3", "Bb3", "D4", "F4"),
        "Abm7" to listOf("Ab3", "B3", "Eb4", "Gb4"), "Am7" to listOf("A3", "C4", "E4", "G4"),
        "Bbm7" to listOf("Bb3", "Db4", "F4", "Ab4"), "Bm7" to listOf("B3", "D4", "Gb4", "A4"),

        // --- Diminished ---
        "Cdim" to listOf("C4", "Eb4", "Gb4"), "Dbdim" to listOf("Db4", "E4", "G4"),
        "Ddim" to listOf("D4", "F4", "Ab4"), "Ebdim" to listOf("Eb4", "Gb4", "A4"),
        "Edim" to listOf("E4", "G4", "Bb4"), "Fdim" to listOf("F3", "Ab3", "B3"),
        "Gbdim" to listOf("Gb3", "A3", "C4"), "Gdim" to listOf("G3", "Bb3", "Db4"),
        "Abdim" to listOf("Ab3", "B3", "D4"), "Adim" to listOf("A3", "C4", "Eb4"),
        "Bbdim" to listOf("Bb3", "Db4", "E4"), "Bdim" to listOf("B3", "D4", "F4"),

        // --- Augmented ---
        "Caug" to listOf("C4", "E4", "Ab4"), "Dbaug" to listOf("Db4", "F4", "A4"),
        "Daug" to listOf("D4", "Gb4", "Bb4"), "Ebaug" to listOf("Eb4", "G4", "B4"),
        "Eaug" to listOf("E3", "Ab3", "C4"), "Faug" to listOf("F3", "A3", "Bb4"),
        "Gbaug" to listOf("Gb3", "Bb3", "D4"), "Gaug" to listOf("G3", "B3", "Eb4"),
        "Abaug" to listOf("Ab3", "C4", "E4"), "Aaug" to listOf("A3", "Bb4", "F4"),
        "Bbaug" to listOf("Bb3", "D4", "Gb4"), "Baug" to listOf("B3", "Eb4", "G4"),

        // --- Sus2 ---
        "Csus2" to listOf("C4", "D4", "G4"), "Dbsus2" to listOf("Db4", "Eb4", "Ab4"),
        "Dsus2" to listOf("D4", "E4", "A4"), "Ebsus2" to listOf("Eb4", "F4", "Bb4"),
        "Esus2" to listOf("E3", "Gb3", "B3"), "Fsus2" to listOf("F3", "G3", "C4"),
        "Gbsus2" to listOf("Gb3", "Ab3", "Db4"), "Gsus2" to listOf("G3", "A3", "D4"),
        "Absus2" to listOf("Ab3", "Bb3", "Eb4"), "Asus2" to listOf("A3", "B3", "E4"),
        "Bbsus2" to listOf("Bb3", "C4", "F4"), "Bsus2" to listOf("B3", "Bb4", "Gb4"),

        // --- Sus4 ---
        "Csus4" to listOf("C4", "F4", "G4"), "Dbsus4" to listOf("Db4", "Gb4", "Ab4"),
        "Dsus4" to listOf("D4", "G4", "A4"), "Ebsus4" to listOf("Eb4", "Ab4", "Bb4"),
        "Esus4" to listOf("E3", "A3", "B3"), "Fsus4" to listOf("F3", "Bb3", "C4"),
        "Gbsus4" to listOf("Gb3", "B3", "Db4"), "Gsus4" to listOf("G3", "C4", "D4"),
        "Absus4" to listOf("Ab3", "Db4", "Eb4"), "Asus4" to listOf("A3", "D4", "E4"),
        "Bbsus4" to listOf("Bb3", "Eb4", "F4"), "Bsus4" to listOf("B3", "E4", "Gb4"),

        // --- 7sus4 ---
        "C7sus4" to listOf("C4", "F4", "G4", "Bb4"), "Db7sus4" to listOf("Db4", "Gb4", "Ab4", "B4"),
        "D7sus4" to listOf("D4", "G4", "A4", "C5"), "Eb7sus4" to listOf("Eb4", "Ab4", "Bb4", "Db5"),
        "E7sus4" to listOf("E3", "A3", "B3", "D4"), "F7sus4" to listOf("F3", "Bb3", "C4", "Eb4"),
        "Gb7sus4" to listOf("Gb3", "B3", "Db4", "E4"), "G7sus4" to listOf("G3", "C4", "D4", "F4"),
        "Ab7sus4" to listOf("Ab3", "Db4", "Eb4", "Gb4"), "A7sus4" to listOf("A3", "D4", "E4", "G4"),
        "Bb7sus4" to listOf("Bb3", "Eb4", "F4", "Ab4"), "B7sus4" to listOf("B3", "E4", "Gb4", "A4"),

        // --- Minor 7 Flat 5 (Half-Diminished) ---
        "Cm7b5" to listOf("C4", "Eb4", "Gb4", "Bb4"), "Dbm7b5" to listOf("Db4", "E4", "G4", "B4"),
        "Dm7b5" to listOf("D4", "F4", "Ab4", "C5"), "Ebm7b5" to listOf("Eb4", "Gb4", "A4", "Db5"),
        "Em7b5" to listOf("E4", "G4", "Bb4", "D5"), "Fm7b5" to listOf("F3", "Ab3", "B3", "Eb4"),
        "Gbm7b5" to listOf("Gb3", "A3", "C4", "E4"), "Gm7b5" to listOf("G3", "Bb3", "Db4", "F4"),
        "Abm7b5" to listOf("Ab3", "B3", "D4", "Gb4"), "Am7b5" to listOf("A3", "C4", "Eb4", "G4"),
        "Bbm7b5" to listOf("Bb3", "Db4", "E4", "Ab4"), "Bm7b5" to listOf("B3", "D4", "F4", "A4"),

        // --- Diminished 7th ---
        "Cdim7" to listOf("C4", "Eb4", "Gb4", "A4"), "Dbdim7" to listOf("Db4", "E4", "G4", "Bb4"),
        "Ddim7" to listOf("D4", "F4", "Ab4", "B4"), "Ebdim7" to listOf("Eb4", "Gb4", "A4", "C5"),
        "Edim7" to listOf("E4", "G4", "Bb4", "Db5"), "Fdim7" to listOf("F3", "Ab3", "B3", "D4"),
        "Gbdim7" to listOf("Gb3", "A3", "C4", "Eb4"), "Gdim7" to listOf("G3", "Bb3", "Db4", "E4"),
        "Abdim7" to listOf("Ab3", "B3", "D4", "F4"), "Adim7" to listOf("A3", "C4", "Eb4", "Gb4"),
        "Bbdim7" to listOf("Bb3", "Db4", "E4", "G4"), "Bdim7" to listOf("B3", "D4", "F4", "Ab4"),

        // --- Major 6th ---
        "C6" to listOf("C4", "E4", "G4", "A4"), "Db6" to listOf("Db4", "F4", "Ab4", "Bb4"),
        "D6" to listOf("D4", "Gb4", "A4", "B4"), "Eb6" to listOf("Eb4", "G4", "Bb4", "C5"),
        "E6" to listOf("E3", "Ab3", "B3", "Db4"), "F6" to listOf("F3", "A3", "C4", "D4"),
        "Gb6" to listOf("Gb3", "Bb3", "Db4", "Eb4"), "G6" to listOf("G3", "B3", "D4", "E4"),
        "Ab6" to listOf("Ab3", "C4", "Eb4", "F4"), "A6" to listOf("A3", "Db4", "E4", "Gb4"),
        "Bb6" to listOf("Bb3", "D4", "F4", "G4"), "B6" to listOf("B3", "Eb4", "Gb4", "Ab4"),

        // --- Minor 6th ---
        "Cm6" to listOf("C4", "Eb4", "G4", "A4"), "Dbm6" to listOf("Db4", "E4", "Ab4", "Bb4"),
        "Dm6" to listOf("D4", "F4", "A4", "B4"), "Ebm6" to listOf("Eb4", "Gb4", "Bb4", "C5"),
        "Em6" to listOf("E3", "G3", "B3", "Db4"), "Fm6" to listOf("F3", "Ab3", "C4", "D4"),
        "Gbm6" to listOf("Gb3", "A3", "Db4", "Eb4"), "Gm6" to listOf("G3", "Bb3", "D4", "E4"),
        "Abm6" to listOf("Ab3", "B3", "Eb4", "F4"), "Am6" to listOf("A3", "C4", "E4", "Gb4"),
        "Bbm6" to listOf("Bb3", "Db4", "F4", "G4"), "Bm6" to listOf("B3", "D4", "Gb4", "Ab4"),

        // --- Add 9 ---
        "Cadd9" to listOf("C4", "E4", "G4", "D5"), "Dbadd9" to listOf("Db4", "F4", "Ab4", "Eb5"),
        "Dadd9" to listOf("D4", "Gb4", "A4", "E5"), "Ebadd9" to listOf("Eb4", "G4", "Bb4", "F5"),
        "Eadd9" to listOf("E3", "Ab3", "B3", "Gb4"), "Fadd9" to listOf("F3", "A3", "C4", "G4"),
        "Gbadd9" to listOf("Gb3", "Bb3", "Db4", "Ab4"), "Gadd9" to listOf("G3", "B3", "D4", "A4"),
        "Abadd9" to listOf("Ab3", "C4", "Eb4", "Bb4"), "Aadd9" to listOf("A3", "Db4", "E4", "B4"),
        "Bbadd9" to listOf("Bb3", "D4", "F4", "C5"), "Badd9" to listOf("B3", "Eb4", "Gb4", "Db5"),

        // --- Minor (Add 9) ---
        "Cm(add9)" to listOf("C4", "Eb4", "G4", "D5"), "Dbm(add9)" to listOf("Db4", "E4", "Ab4", "Eb5"),
        "Dm(add9)" to listOf("D4", "F4", "A4", "E5"), "Ebm(add9)" to listOf("Eb4", "Gb4", "Bb4", "F5"),
        "Em(add9)" to listOf("E3", "G3", "B3", "Gb4"), "Fm(add9)" to listOf("F3", "Ab3", "C4", "G4"),
        "Gbm(add9)" to listOf("Gb3", "A3", "Db4", "Ab4"), "Gm(add9)" to listOf("G3", "Bb3", "D4", "A4"),
        "Abm(add9)" to listOf("Ab3", "B3", "Eb4", "Bb4"), "Am(add9)" to listOf("A3", "C4", "E4", "B4"),
        "Bbm(add9)" to listOf("Bb3", "Db4", "F4", "C5"), "Bm(add9)" to listOf("B3", "D4", "Gb4", "Db5"),

        // --- Dominant 9th ---
        "C9" to listOf("C4", "E4", "G4", "Bb4", "D5"), "Db9" to listOf("Db4", "F4", "Ab4", "B4", "Eb5"),
        "D9" to listOf("D4", "Gb4", "A4", "C5", "E5"), "Eb9" to listOf("Eb4", "G4", "Bb4", "Db5", "F5"),
        "E9" to listOf("E3", "Ab3", "B3", "D4", "Gb4"), "F9" to listOf("F3", "A3", "C4", "Eb4", "G4"),
        "Gb9" to listOf("Gb3", "Bb3", "Db4", "E4", "Ab4"), "G9" to listOf("G3", "B3", "D4", "F4", "A4"),
        "Ab9" to listOf("Ab3", "C4", "Eb4", "Gb4", "Bb4"), "A9" to listOf("A3", "Db4", "E4", "G4", "B4"),
        "Bb9" to listOf("Bb3", "D4", "F4", "Ab4", "C5"), "B9" to listOf("B3", "Eb4", "Gb4", "A4", "Db5"),

        // --- Major 9th ---
        "Cmaj9" to listOf("C4", "E4", "G4", "B4", "D5"), "Dbmaj9" to listOf("Db4", "F4", "Ab4", "C5", "Eb5"),
        "Dmaj9" to listOf("D4", "Gb4", "A4", "Db5", "E5"), "Ebmaj9" to listOf("Eb4", "G4", "Bb4", "D5", "F5"),
        "Emaj9" to listOf("E3", "Ab3", "B3", "Eb4", "Gb4"), "Fmaj9" to listOf("F3", "A3", "C4", "E4", "G4"),
        "Gbmaj9" to listOf("Gb3", "Bb3", "Db4", "F4", "Ab4"), "Gmaj9" to listOf("G3", "B3", "D4", "Gb4", "A4"),
        "Abmaj9" to listOf("Ab3", "C4", "Eb4", "G4", "Bb4"), "Amaj9" to listOf("A3", "Db4", "E4", "Ab4", "B4"),
        "Bbmaj9" to listOf("Bb3", "D4", "F4", "A4", "C5"), "Bmaj9" to listOf("B3", "Eb4", "Gb4", "Bb4", "Db5"),

        // --- Minor 9th ---
        "Cm9" to listOf("C4", "Eb4", "G4", "Bb4", "D5"), "Dbm9" to listOf("Db4", "E4", "Ab4", "B4", "Eb5"),
        "Dm9" to listOf("D4", "F4", "A4", "C5", "E5"), "Ebm9" to listOf("Eb4", "Gb4", "Bb4", "Db5", "F5"),
        "Em9" to listOf("E3", "G3", "B3", "D4", "Gb4"), "Fm9" to listOf("F3", "Ab3", "C4", "Eb4", "G4"),
        "Gbm9" to listOf("Gb3", "A3", "Db4", "E4", "Ab4"), "Gm9" to listOf("G3", "Bb3", "D4", "F4", "A4"),
        "Abm9" to listOf("Ab3", "B3", "Eb4", "Gb4", "Bb4"), "Am9" to listOf("A3", "C4", "E4", "G4", "B4"),
        "Bbm9" to listOf("Bb3", "Db4", "F4", "Ab4", "C5"), "Bm9" to listOf("B3", "D4", "Gb4", "A4", "Db5"),

        // --- Dominant 11th (Voiced as R-5-b7-9-11) ---
        "C11" to listOf("C4", "G4", "Bb4", "D5", "F5"), "Db11" to listOf("Db4", "Ab4", "B4", "Eb5", "Gb5"),
        "D11" to listOf("D4", "A4", "C5", "E5", "G5"), "Eb11" to listOf("Eb4", "Bb4", "Db5", "F5", "Ab5"),
        "E11" to listOf("E3", "B3", "D4", "Gb4", "A4"), "F11" to listOf("F3", "C4", "Eb4", "G4", "Bb4"),
        "Gb11" to listOf("Gb3", "Db4", "E4", "Ab4", "B4"), "G11" to listOf("G3", "D4", "F4", "A4", "C5"),
        "Ab11" to listOf("Ab3", "Eb4", "Gb4", "Bb4", "Db5"), "A11" to listOf("A3", "E4", "G4", "B4", "D5"),
        "Bb11" to listOf("Bb3", "F4", "Ab4", "C5", "Eb5"), "B11" to listOf("B3", "Gb4", "A4", "Db5", "E5"),

        // --- Dominant 13th (Voiced as R-3-b7-9-13) ---
        "C13" to listOf("C4", "E4", "Bb4", "D5", "A5"), "Db13" to listOf("Db4", "F4", "B4", "Eb5", "Bb5"),
        "D13" to listOf("D4", "Gb4", "C5", "E5", "B5"), "Eb13" to listOf("Eb4", "G4", "Db5", "F5", "C6"),
        "E13" to listOf("E3", "Ab3", "D4", "Gb4", "Db5"), "F13" to listOf("F3", "A3", "Eb4", "G4", "D5"),
        "Gb13" to listOf("Gb3", "Bb3", "E4", "Ab4", "Eb5"), "G13" to listOf("G3", "B3", "F4", "A4", "E5"),
        "Ab13" to listOf("Ab3", "C4", "Gb4", "Bb4", "F5"), "A13" to listOf("A3", "Db4", "G4", "B4", "Gb5"),
        "Bb13" to listOf("Bb3", "D4", "Ab4", "C5", "G5"), "B13" to listOf("B3", "Eb4", "A4", "Db5", "Ab5"),
    )

    val GUITAR_CHORD_VOICINGS = mapOf(
        // --- Major ---
        "C" to listOf("C3", "E3", "G3", "C4", "E4"), "Db" to listOf("Db3", "Ab3", "Db4", "F4"),
        "D" to listOf("D3", "A3", "D4", "Gb4"), "Eb" to listOf("Eb3", "Bb3", "Eb4", "G4", "Bb4"),
        "E" to listOf("E2", "B2", "E3", "Ab3", "B3", "E4"), "F" to listOf("F2", "C3", "F3", "A3", "C4", "F4"),
        "Gb" to listOf("Gb2", "Db3", "Gb3", "Bb3", "Db4", "Gb4"), "G" to listOf("G2", "B2", "D3", "G3", "B3", "G4"),
        "Ab" to listOf("Ab2", "Eb3", "Ab3", "C4", "Eb4", "Ab4"), "A" to listOf("A2", "E3", "A3", "Db4", "E4"),
        "Bb" to listOf("Bb2", "F3", "Bb3", "D4", "F4"), "B" to listOf("B2", "Gb3", "B3", "Eb4", "Gb4"),
        // --- Minor ---
        "Cm" to listOf("C3", "G3", "C4", "Eb4", "G4"), "Dbm" to listOf("Db3", "Ab3", "Db4", "E4", "Ab4"),
        "Dm" to listOf("D3", "A3", "D4", "F4"), "Ebm" to listOf("Eb3", "Bb3", "Eb4", "Gb4", "Bb4"),
        "Em" to listOf("E2", "B2", "E3", "G3", "B3", "E4"), "Fm" to listOf("F2", "C3", "F3", "Ab3", "C4", "F4"),
        "Gbm" to listOf("Gb2", "Db3", "Gb3", "A3", "Db4", "Gb4"), "Gm" to listOf("G2", "D3", "G3", "Bb3", "D4", "G4"),
        "Abm" to listOf("Ab2", "Eb3", "Ab3", "B3", "Eb4", "Ab4"), "Am" to listOf("A2", "E3", "A3", "C4", "E4"),
        "Bbm" to listOf("Bb2", "F3", "Bb3", "Db4", "F4"), "Bm" to listOf("B2", "Gb3", "B3", "D4", "Gb4"),
        // --- Dominant 7th ---
        "C7" to listOf("C3", "E3", "G3", "Bb3", "E4"), "Db7" to listOf("Db3", "Gb3", "B3", "Eb4", "Gb4"),
        "D7" to listOf("D3", "A3", "C4", "Gb4"), "Eb7" to listOf("Eb3", "Bb3", "Db4", "G4"),
        "E7" to listOf("E2", "B2", "D3", "Ab3", "B3", "E4"), "F7" to listOf("F2", "C3", "Eb3", "A3", "C4", "F4"),
        "Gb7" to listOf("Gb2", "Db3", "E3", "Bb3", "Db4", "Gb4"), "G7" to listOf("G2", "B2", "D3", "G3", "B3", "F4"),
        "Ab7" to listOf("Ab2", "Eb3", "Gb3", "C4", "Eb4", "Ab4"), "A7" to listOf("A2", "E3", "G3", "Db4", "E4"),
        "Bb7" to listOf("Bb2", "F3", "Ab3", "Db4", "F4"), "B7" to listOf("B2", "Eb3", "A3", "B3", "Gb4"),
        // --- Major 7th ---
        "Cmaj7" to listOf("C3", "E3", "G3", "B3", "E4"), "Dbmaj7" to listOf("Db3", "Ab3", "C4", "F4"),
        "Dmaj7" to listOf("D3", "A3", "Db4", "Gb4"), "Ebmaj7" to listOf("Eb3", "Bb3", "D4", "G4"),
        "Emaj7" to listOf("E2", "B2", "Eb3", "Ab3", "B3", "E4"), "Fmaj7" to listOf("F2", "C3", "E3", "A3"),
        "Gbmaj7" to listOf("Gb2", "Db3", "F3", "Bb3", "Db4", "Gb4"), "Gmaj7" to listOf("G2", "B2", "D3", "G3", "B3", "Gb4"),
        "Abmaj7" to listOf("Ab2", "Eb3", "G3", "C4", "Eb4", "Ab4"), "Amaj7" to listOf("A2", "E3", "Ab3", "Db4", "E4"),
        "Bbmaj7" to listOf("Bb2", "F3", "A3", "D4", "F4"), "Bmaj7" to listOf("B2", "Gb3", "Bb3", "Eb4"),
        // --- Minor 7th ---
        "Cm7" to listOf("C3", "G3", "Bb3", "Eb4"), "Dbm7" to listOf("Db3", "Ab3", "B3", "E4", "Ab4"),
        "Dm7" to listOf("D3", "A3", "C4", "F4"), "Ebm7" to listOf("Eb3", "Bb3", "Db4", "Gb4", "Bb4"),
        "Em7" to listOf("E2", "B2", "E3", "G3", "D4", "E4"), "Fm7" to listOf("F2", "C3", "Ab3", "Eb4"),
        "Gbm7" to listOf("Gb2", "Db3", "E3", "A3", "Db4", "Gb4"), "Gm7" to listOf("G2", "D3", "F3", "Bb3"),
        "Abm7" to listOf("Ab2", "Eb3", "Gb3", "B3", "Eb4", "Ab4"), "Am7" to listOf("A2", "E3", "G3", "C4", "E4"),
        "Bbm7" to listOf("Bb2", "F3", "Ab3", "C4", "F4"), "Bm7" to listOf("B2", "A3", "D4", "Gb4", "B4"),
        // --- Diminished ---
        "Cdim" to listOf("C3", "Eb3", "Gb3"), "Dbdim" to listOf("Db3", "E3", "G3"),
        "Ddim" to listOf("D3", "F3", "Ab3"), "Ebdim" to listOf("Eb3", "Gb3", "A3"),
        "Edim" to listOf("E2", "Bb2", "Eb3"), "Fdim" to listOf("F3", "Ab3", "B3"),
        "Gbdim" to listOf("Gb3", "A3", "C4"), "Gdim" to listOf("G3", "Bb3", "Db4"),
        "Abdim" to listOf("Ab2", "B2", "D3"), "Adim" to listOf("A2", "C3", "Eb3"),
        "Bbdim" to listOf("Bb2", "Db3", "E3"), "Bdim" to listOf("B2", "D3", "F3"),
        // --- Augmented ---
        "Caug" to listOf("C3", "E3", "Ab3"), "Dbaug" to listOf("Db3", "F3", "A3"),
        "Daug" to listOf("D3", "Gb3", "Bb3"), "Ebaug" to listOf("Eb3", "G3", "B3"),
        "Eaug" to listOf("E3", "Ab3", "C4"), "Faug" to listOf("F3", "A3", "Db4"),
        "Gbaug" to listOf("Gb2", "Bb2", "D3"), "Gaug" to listOf("G2", "B2", "Eb3"),
        "Abaug" to listOf("Ab3", "C4", "E4"), "Aaug" to listOf("A2", "Db3", "F3"),
        "Bbaug" to listOf("Bb2", "D3", "Gb3"), "Baug" to listOf("B2", "Eb3", "G3"),
        // --- Sus2 ---
        "Csus2" to listOf("C3", "D3", "G3"), "Dbsus2" to listOf("Db3", "Ab3", "Eb4", "Ab4"),
        "Dsus2" to listOf("D3", "A3", "D4", "E4"), "Ebsus2" to listOf("Eb4", "F4", "Bb4"),
        "Esus2" to listOf("E2", "B2", "Gb3", "B3", "E4"), "Fsus2" to listOf("F2", "C3", "F3", "G3", "C4", "F4"),
        "Gbsus2" to listOf("Gb2", "Db3", "Gb3", "Ab3", "Db4", "Gb4"), "Gsus2" to listOf("G2", "A2", "D3", "G3"),
        "Absus2" to listOf("Ab2", "Eb3", "Ab3", "Bb3", "Eb4", "Ab4"), "Asus2" to listOf("A2", "E3", "A3", "B3", "E4"),
        "Bbsus2" to listOf("Bb2", "F3", "Bb3", "C4", "F4"), "Bsus2" to listOf("B2", "Gb3", "B3", "Db4", "Gb4"),
        // --- Sus4 ---
        "Csus4" to listOf("C3", "F3", "G3"), "Dbsus4" to listOf("Db3", "Ab3", "Db4", "Gb4", "Ab4"),
        "Dsus4" to listOf("D3", "A3", "D4", "G4"), "Ebsus4" to listOf("Eb3", "Bb3", "Eb4", "Ab4", "Bb4"),
        "Esus4" to listOf("E2", "B2", "E3", "A3", "B3", "E4"), "Fsus4" to listOf("F2", "C3", "F3", "Bb3", "C4", "F4"),
        "Gbsus4" to listOf("Gb2", "Db3", "Gb3", "B3", "Db4", "Gb4"), "Gsus4" to listOf("G2", "C3", "D3", "G3"),
        "Absus4" to listOf("Ab2", "Eb3", "Ab3", "Db4", "Eb4", "Ab4"), "Asus4" to listOf("A2", "E3", "A3", "D4", "E4"),
        "Bbsus4" to listOf("Bb2", "F3", "Bb3", "Eb4", "F4"), "Bsus4" to listOf("B2", "Gb3", "B3", "E4", "Gb4"),
        // --- 7sus4 ---
        "C7sus4" to listOf("C3", "G3", "Bb3", "F4"), "Db7sus4" to listOf("Db3", "Ab3", "B3", "Gb4"),
        "D7sus4" to listOf("D3", "A3", "C4", "G4"), "Eb7sus4" to listOf("Eb3", "Bb3", "Db4", "Ab4"),
        "E7sus4" to listOf("E2", "B2", "D3", "A3", "B3", "E4"), "F7sus4" to listOf("F2", "C3", "Eb3", "Bb3"),
        "Gb7sus4" to listOf("Gb2", "Db3", "E3", "B3"), "G7sus4" to listOf("G2", "D3", "F3", "C4"),
        "Ab7sus4" to listOf("Ab2", "Eb3", "Gb3", "Db4"), "A7sus4" to listOf("A2", "E3", "G3", "D4"),
        "Bb7sus4" to listOf("Bb2", "F3", "Ab3", "Eb4"), "B7sus4" to listOf("B2", "Gb3", "A3", "E4"),
        // --- m7b5 ---
        "Cm7b5" to listOf("C3", "Gb3", "Bb3", "Eb4"), "Dbm7b5" to listOf("Db3", "G3", "B3", "E4"),
        "Dm7b5" to listOf("D3", "Ab3", "C4", "F4"), "Ebm7b5" to listOf("Eb3", "A3", "Db4", "Gb4"),
        "Em7b5" to listOf("E2", "Bb2", "E3", "G3", "D4"), "Fm7b5" to listOf("F2", "B2", "Eb3", "Ab3"),
        "Gbm7b5" to listOf("Gb2", "C3", "E3", "A3"), "Gm7b5" to listOf("G2", "Db3", "F3", "Bb3"),
        "Abm7b5" to listOf("Ab2", "D3", "Gb3", "B3"), "Am7b5" to listOf("A2", "Eb3", "G3", "C4"),
        "Bbm7b5" to listOf("Bb2", "E3", "Ab3", "Db4"), "Bm7b5" to listOf("B2", "F3", "A3", "D4"),
        // --- dim7 ---
        "Cdim7" to listOf("C3", "Gb3", "A3", "Eb4"), "Dbdim7" to listOf("Db3", "G3", "A3", "E4"),
        "Ddim7" to listOf("D3", "Ab3", "B3", "F4"), "Ebdim7" to listOf("Eb3", "A3", "C4", "Gb4"),
        "Edim7" to listOf("E3", "Bb3", "Db4", "G4"), "Fdim7" to listOf("F2", "B2", "Db3", "Ab3"),
        "Gbdim7" to listOf("Gb2", "C3", "D3", "A3"), "Gdim7" to listOf("G2", "Db3", "E3", "Bb3"),
        "Abdim7" to listOf("Ab2", "D3", "F3", "B3"), "Adim7" to listOf("A2", "Eb3", "Gb3", "C4"),
        "Bbdim7" to listOf("Bb2", "E3", "G3", "Db4"), "Bdim7" to listOf("B2", "F3", "Ab3", "D4")
    )

    val GUITAR_CHORD_TABS: Map<String, List<Any>> = mapOf(
        // --- Major ---
        "C" to listOf('x', 3, 2, 0, 1, 0), "Db" to listOf('x', 4, 6, 6, 6, 4),
        "D" to listOf('x', 'x', 0, 2, 3, 2), "Eb" to listOf('x', 6, 8, 8, 8, 6),
        "E" to listOf(0, 2, 2, 1, 0, 0), "F" to listOf(1, 3, 3, 2, 1, 1),
        "Gb" to listOf(2, 4, 4, 3, 2, 2), "G" to listOf(3, 2, 0, 0, 0, 3),
        "Ab" to listOf(4, 6, 6, 5, 4, 4), "A" to listOf('x', 0, 2, 2, 2, 0),
        "Bb" to listOf('x', 1, 3, 3, 3, 1), "B" to listOf('x', 2, 4, 4, 4, 2),
        // --- Minor ---
        "Cm" to listOf('x', 3, 5, 5, 4, 3), "Dbm" to listOf('x', 4, 6, 6, 5, 4),
        "Dm" to listOf('x', 'x', 0, 2, 3, 1), "Ebm" to listOf('x', 6, 8, 8, 7, 6),
        "Em" to listOf(0, 2, 2, 0, 0, 0), "Fm" to listOf(1, 3, 3, 1, 1, 1),
        "Gbm" to listOf(2, 4, 4, 2, 2, 2), "Gm" to listOf(3, 5, 5, 3, 3, 3),
        "Abm" to listOf(4, 6, 6, 4, 4, 4), "Am" to listOf('x', 0, 2, 2, 1, 0),
        "Bbm" to listOf('x', 1, 3, 3, 2, 1), "Bm" to listOf('x', 2, 4, 4, 3, 2),
        // --- Dominant 7th ---
        "C7" to listOf('x', 3, 2, 3, 1, 0), "Db7" to listOf('x', 4, 6, 4, 6, 4),
        "D7" to listOf('x', 'x', 0, 2, 1, 2), "Eb7" to listOf('x', 6, 5, 6, 4, 'x'),
        "E7" to listOf(0, 2, 0, 1, 0, 0), "F7" to listOf(1, 3, 1, 2, 1, 1),
        "Gb7" to listOf(2, 4, 2, 3, 2, 2), "G7" to listOf(3, 2, 0, 0, 0, 1),
        "Ab7" to listOf(4, 6, 4, 5, 4, 4), "A7" to listOf('x', 0, 2, 0, 2, 0),
        "Bb7" to listOf('x', 1, 3, 1, 3, 1), "B7" to listOf('x', 2, 1, 2, 0, 2),
        // --- Major 7th ---
        "Cmaj7" to listOf('x', 3, 2, 0, 0, 0), "Dbmaj7" to listOf('x', 4, 6, 5, 6, 'x'),
        "Dmaj7" to listOf('x', 'x', 0, 2, 2, 2), "Ebmaj7" to listOf('x', 6, 8, 7, 8, 'x'),
        "Emaj7" to listOf(0, 2, 1, 1, 0, 0), "Fmaj7" to listOf(1, 3, 2, 2, 1, 1),
        "Gbmaj7" to listOf(2, 4, 3, 3, 2, 2), "Gmaj7" to listOf(3, 2, 0, 0, 0, 2),
        "Abmaj7" to listOf(4, 6, 5, 5, 4, 4), "Amaj7" to listOf('x', 0, 2, 1, 2, 0),
        "Bbmaj7" to listOf('x', 1, 3, 2, 3, 1), "Bmaj7" to listOf('x', 2, 4, 3, 4, 2),
        // --- Minor 7th ---
        "Cm7" to listOf('x', 3, 5, 3, 4, 3), "Dbm7" to listOf('x', 4, 6, 4, 5, 4),
        "Dm7" to listOf('x', 'x', 0, 2, 1, 1), "Ebm7" to listOf('x', 6, 8, 6, 7, 6),
        "Em7" to listOf(0, 2, 2, 0, 3, 0), "Fm7" to listOf(1, 3, 1, 1, 1, 1),
        "Gbm7" to listOf(2, 4, 2, 2, 2, 2), "Gm7" to listOf(3, 5, 3, 3, 3, 3),
        "Abm7" to listOf(4, 6, 4, 4, 4, 4), "Am7" to listOf('x', 0, 2, 0, 1, 0),
        "Bbm7" to listOf('x', 1, 3, 1, 2, 1), "Bm7" to listOf('x', 2, 4, 2, 3, 2),
        // --- Diminished ---
        "Cdim" to listOf('x', 3, 4, 5, 4, 'x'), "Dbdim" to listOf('x', 4, 5, 6, 5, 'x'),
        "Ddim" to listOf('x', 'x', 0, 1, 3, 1), "Ebdim" to listOf('x', 'x', 1, 2, 4, 2),
        "Edim" to listOf(0, 1, 2, 0, 'x', 'x'), "Fdim" to listOf(1, 2, 3, 1, 'x', 'x'),
        "Gbdim" to listOf(2, 3, 4, 2, 'x', 'x'), "Gdim" to listOf(3, 4, 5, 3, 'x', 'x'),
        "Abdim" to listOf(4, 5, 6, 4, 'x', 'x'), "Adim" to listOf('x', 0, 1, 2, 1, 'x'),
        "Bbdim" to listOf('x', 1, 2, 3, 2, 'x'), "Bdim" to listOf('x', 2, 3, 4, 3, 'x'),
        // --- Augmented ---
        "Caug" to listOf('x', 3, 2, 1, 1, 'x'), "Dbaug" to listOf('x', 4, 3, 2, 2, 'x'),
        "Daug" to listOf('x', 'x', 0, 3, 3, 2), "Ebaug" to listOf('x', 6, 5, 4, 4, 'x'),
        "Eaug" to listOf('x', 'x', 2, 1, 1, 0), "Faug" to listOf('x', 'x', 3, 2, 2, 1),
        "Gbaug" to listOf('x', 'x', 4, 3, 3, 2), "Gaug" to listOf(3, 2, 1, 0, 0, 3),
        "Abaug" to listOf('x', 'x', 6, 5, 5, 4), "Aaug" to listOf('x', 0, 3, 2, 2, 1),
        "Bbaug" to listOf('x', 1, 0, 3, 3, 2), "Baug" to listOf('x', 2, 1, 0, 0, 'x'),
        // --- Sus2 ---
        "Csus2" to listOf('x', 3, 5, 5, 3, 'x'), "Dbsus2" to listOf('x', 4, 6, 6, 4, 'x'),
        "Dsus2" to listOf('x', 'x', 0, 2, 3, 0), "Ebsus2" to listOf('x', 'x', 1, 3, 4, 1),
        "Esus2" to listOf(0, 2, 4, 4, 0, 0), "Fsus2" to listOf(1, 3, 3, 0, 1, 1),
        "Gbsus2" to listOf(2, 4, 4, 1, 2, 2), "Gsus2" to listOf(3, 0, 0, 0, 3, 'x'),
        "Absus2" to listOf(4, 6, 6, 4, 4, 4), "Asus2" to listOf('x', 0, 2, 2, 0, 0),
        "Bbsus2" to listOf('x', 1, 3, 3, 1, 1), "Bsus2" to listOf('x', 2, 4, 4, 2, 2),
        // --- Sus4 ---
        "Csus4" to listOf('x', 3, 3, 0, 1, 1), "Dbsus4" to listOf('x', 4, 6, 6, 7, 4),
        "Dsus4" to listOf('x', 'x', 0, 2, 3, 3), "Ebsus4" to listOf('x', 6, 8, 8, 9, 6),
        "Esus4" to listOf(0, 2, 2, 2, 0, 0), "Fsus4" to listOf(1, 3, 3, 3, 1, 1),
        "Gbsus4" to listOf(2, 4, 4, 4, 2, 2), "Gsus4" to listOf(3, 5, 5, 5, 3, 3),
        "Absus4" to listOf(4, 6, 6, 6, 4, 4), "Asus4" to listOf('x', 0, 2, 2, 3, 0),
        "Bbsus4" to listOf('x', 1, 3, 3, 4, 1), "Bsus4" to listOf('x', 2, 4, 4, 5, 2),
        // --- 7sus4 ---
        "C7sus4" to listOf('x', 3, 5, 3, 6, 3), "Db7sus4" to listOf('x', 4, 6, 4, 7, 4),
        "D7sus4" to listOf('x', 'x', 0, 2, 1, 3), "Eb7sus4" to listOf('x', 6, 8, 6, 9, 6),
        "E7sus4" to listOf(0, 2, 0, 2, 0, 0), "F7sus4" to listOf(1, 3, 1, 3, 1, 1),
        "Gb7sus4" to listOf(2, 4, 2, 4, 2, 2), "G7sus4" to listOf(3, 5, 3, 5, 3, 3),
        "Ab7sus4" to listOf(4, 6, 4, 6, 4, 4), "A7sus4" to listOf('x', 0, 2, 0, 3, 0),
        "Bb7sus4" to listOf('x', 1, 3, 1, 4, 1), "B7sus4" to listOf('x', 2, 4, 2, 5, 2),
        // --- m7b5 ---
        "Cm7b5" to listOf('x', 3, 4, 3, 4, 'x'), "Dbm7b5" to listOf('x', 4, 5, 4, 5, 'x'),
        "Dm7b5" to listOf('x', 'x', 0, 1, 1, 1), "Ebm7b5" to listOf('x', 6, 7, 6, 7, 'x'),
        "Em7b5" to listOf(0, 1, 2, 0, 3, 0), "Fm7b5" to listOf(1, 'x', 1, 1, 0, 'x'),
        "Gbm7b5" to listOf(2, 'x', 2, 2, 1, 'x'), "Gm7b5" to listOf(3, 'x', 3, 3, 2, 'x'),
        "Abm7b5" to listOf(4, 'x', 4, 4, 3, 'x'), "Am7b5" to listOf('x', 0, 1, 0, 1, 'x'),
        "Bbm7b5" to listOf('x', 1, 2, 1, 2, 'x'), "Bm7b5" to listOf('x', 2, 3, 2, 3, 'x'),
        // --- dim7 ---
        "Cdim7" to listOf('x', 3, 4, 2, 4, 'x'), "Dbdim7" to listOf('x', 4, 5, 3, 5, 'x'),
        "Ddim7" to listOf('x', 'x', 0, 1, 0, 1), "Ebdim7" to listOf('x', 'x', 1, 2, 1, 2),
        "Edim7" to listOf('x', 'x', 2, 3, 2, 3), "Fdim7" to listOf(1, 'x', 0, 1, 0, 'x'),
        "Gbdim7" to listOf(2, 'x', 1, 2, 1, 'x'), "Gdim7" to listOf(3, 'x', 2, 3, 2, 'x'),
        "Abdim7" to listOf(4, 'x', 3, 4, 3, 'x'), "Adim7" to listOf('x', 0, 1, 2, 1, 2),
        "Bbdim7" to listOf('x', 1, 2, 0, 2, 0), "Bdim7" to listOf('x', 2, 3, 1, 3, 1),
        // --- Major 6th ---
        "C6" to listOf('x', 3, 2, 2, 1, 0), "Db6" to listOf('x', 4, 3, 3, 2, 'x'),
        "D6" to listOf('x', 'x', 0, 2, 0, 2), "Eb6" to listOf('x', 6, 5, 5, 4, 'x'),
        "E6" to listOf(0, 2, 2, 1, 2, 0), "F6" to listOf(1, 3, 3, 2, 3, 1),
        "Gb6" to listOf(2, 4, 4, 3, 4, 2), "G6" to listOf(3, 2, 0, 0, 0, 0),
        "Ab6" to listOf(4, 6, 6, 5, 6, 4), "A6" to listOf('x', 0, 2, 2, 2, 2),
        "Bb6" to listOf('x', 1, 3, 3, 3, 3), "B6" to listOf('x', 2, 4, 4, 4, 4),

        // --- Minor 6th ---
        "Cm6" to listOf('x', 3, 1, 2, 1, 'x'), "Dbm6" to listOf('x', 4, 2, 3, 2, 'x'),
        "Dm6" to listOf('x', 'x', 0, 2, 0, 1), "Ebm6" to listOf('x', 6, 4, 5, 4, 'x'),
        "Em6" to listOf(0, 2, 2, 0, 2, 0), "Fm6" to listOf(1, 'x', 0, 1, 1, 1),
        "Gbm6" to listOf(2, 'x', 1, 2, 2, 2), "Gm6" to listOf(3, 'x', 2, 3, 3, 3),
        "Abm6" to listOf(4, 'x', 3, 4, 4, 4), "Am6" to listOf('x', 0, 2, 2, 1, 2),
        "Bbm6" to listOf('x', 1, 3, 0, 2, 'x'), "Bm6" to listOf('x', 2, 4, 1, 3, 'x'),

        // --- Add 9 ---
        "Cadd9"  to listOf('x', 3, 2, 0, 3, 0),
        "Dbadd9" to listOf('x', 4, 6, 8, 6, 4),
        "Dadd9"  to listOf('x', 5, 7, 9, 7, 5),
        "Ebadd9" to listOf('x', 6, 8, 10, 8, 6),
        "Eadd9"  to listOf(0, 2, 2, 1, 0, 2),
        "Fadd9"  to listOf(1, 3, 3, 2, 1, 3),
        "Gbadd9" to listOf(2, 4, 4, 3, 2, 4),
        "Gadd9"  to listOf(3, 2, 0, 2, 0, 3),
        "Abadd9" to listOf(4, 6, 6, 5, 4, 6),
        "Aadd9"  to listOf('x', 0, 2, 4, 2, 0),
        "Bbadd9" to listOf('x', 1, 3, 5, 3, 1),
        "Badd9"  to listOf('x', 2, 4, 6, 4, 2),

        // --- Minor (Add 9) ---
        "Cm(add9)"   to listOf('x', 3, 1, 0, 3, 'x'),
        "Dbm(add9)"  to listOf('x', 4, 2, 1, 4, 'x'),
        "Dm(add9)"   to listOf('x', 5, 3, 2, 5, 'x'),
        "Ebm(add9)"  to listOf('x', 6, 4, 3, 6, 'x'),
        "Em(add9)"   to listOf(0, 2, 2, 0, 0, 2),
        "Fm(add9)"   to listOf(1, 3, 3, 1, 1, 3),
        "Gbm(add9)"  to listOf(2, 4, 4, 2, 2, 4),
        "Gm(add9)"   to listOf(3, 5, 5, 3, 3, 5),
        "Abm(add9)"  to listOf(4, 6, 6, 4, 4, 6),
        "Am(add9)"   to listOf(5, 7, 7, 5, 5, 7),
        "Bbm(add9)"  to listOf(6, 8, 8, 6, 6, 8),
        "Bm(add9)"   to listOf(7, 9, 9, 7, 7, 9),

        // --- Dominant 9th ---
        "C9" to listOf('x', 3, 2, 3, 3, 3), "Db9" to listOf('x', 4, 3, 4, 4, 4),
        "D9" to listOf('x', 5, 4, 5, 5, 5), "Eb9" to listOf('x', 6, 5, 6, 6, 6),
        "E9" to listOf(0, 2, 0, 1, 0, 2), "F9" to listOf(1, 3, 1, 2, 1, 3),
        "Gb9" to listOf(2, 4, 2, 3, 2, 4), "G9" to listOf(3, 5, 3, 4, 3, 5),
        "Ab9" to listOf(4, 6, 4, 5, 4, 6), "A9" to listOf('x', 0, 2, 4, 2, 3),
        "Bb9" to listOf('x', 1, 0, 1, 1, 1), "B9" to listOf('x', 2, 1, 2, 2, 2),

        // --- Major 9th (maj9) ---
        "Cmaj9"  to listOf('x', 3, 2, 4, 3, 'x'), // Correct (C-Shape)
        "Dbmaj9" to listOf('x', 4, 3, 5, 4, 'x'),
        "Dmaj9"  to listOf('x', 5, 4, 6, 5, 'x'),
        "Ebmaj9" to listOf('x', 6, 5, 7, 6, 'x'),
        "Emaj9"  to listOf(0, 2, 1, 1, 0, 2),     // Beautiful Open Voicing
        "Fmaj9"  to listOf('x', 8, 7, 9, 8, 'x'), // FIXED: Using C-shape at 8th fret for consistency
        "Gbmaj9" to listOf(2, 'x', 3, 3, 2, 4),   // FIXED: Added pinky on high E for the 9th
        "Gmaj9"  to listOf(3, 'x', 4, 4, 3, 5),   // FIXED: Added pinky on high E
        "Abmaj9" to listOf(4, 'x', 5, 5, 4, 6),   // FIXED: Added pinky on high E
        "Amaj9"  to listOf('x', 0, 2, 1, 2, 0),   // FIXED: Added C# (3rd) on B string
        "Bbmaj9" to listOf('x', 1, 3, 2, 1, 1),   // FIXED: Removed open E (#11)
        "Bmaj9"  to listOf('x', 2, 1, 3, 2, 'x'), // FIXED: Used C-shape (shifted down) to include C#

        // --- Minor 9th (m9) ---
        "Cm9" to listOf('x', 3, 1, 3, 3, 'x'), "Dbm9" to listOf('x', 4, 2, 4, 4, 'x'),
        "Dm9" to listOf('x', 5, 3, 5, 5, 'x'), "Ebm9" to listOf('x', 6, 4, 6, 6, 'x'),
        "Em9" to listOf(0, 2, 0, 0, 0, 2), "Fm9" to listOf(1, 3, 1, 1, 1, 3),
        "Gbm9" to listOf(2, 4, 2, 2, 2, 4), "Gm9" to listOf(3, 5, 3, 3, 3, 5),
        "Abm9" to listOf(4, 6, 4, 4, 4, 6), "Am9" to listOf(5, 7, 5, 5, 5, 7), // or x 0 2 4 1 0
        "Bbm9" to listOf(6, 8, 6, 6, 6, 8), "Bm9" to listOf(7, 9, 7, 7, 7, 9), // or x 2 0 2 2 2

        // --- Dominant 11th (11) ---
// Strategy: Using "Slash Chord" voicings (e.g. Bb/C) which are the industry standard for "11".
// They sound lush (R-5-b7-9-11) and are very easy to play (often just a barre).
        "C11"  to listOf('x', 3, 3, 3, 3, 3), // Bb/C
        "Db11" to listOf('x', 4, 4, 4, 4, 4),
        "D11"  to listOf('x', 5, 5, 5, 5, 5),
        "Eb11" to listOf('x', 6, 6, 6, 6, 6),
        "E11"  to listOf(0, 2, 0, 2, 0, 0),   // E7sus4 (The standard rock/pop E11)
        "F11"  to listOf(1, 3, 1, 3, 1, 1),   // F7sus4 (Standard F11)
        "Gb11" to listOf(2, 4, 2, 4, 2, 2),
        "G11"  to listOf(3, 5, 3, 5, 3, 3),   // G7sus4 (Standard G11)
        "Ab11" to listOf(4, 6, 4, 6, 4, 4),
        "A11"  to listOf('x', 0, 0, 0, 0, 0), // A7sus4 (Standard A11)
        "Bb11" to listOf('x', 1, 1, 1, 1, 1), // Ab/Bb
        "B11"  to listOf('x', 2, 2, 2, 2, 2), // A/B

// --- Dominant 13th (13) ---
// Strategy: Using "Shell Voicings" for Low-E roots to avoid impossible stretches.
// Strategy: Using "Pinky Extension" for A-string roots (Lush Jazz sound).
        "C13"  to listOf('x', 3, 2, 3, 3, 5), // Classic Jazz (R-3-b7-9-13)
        "Db13" to listOf('x', 4, 3, 4, 4, 6),
        "D13"  to listOf('x', 5, 4, 5, 5, 7),
        "Eb13" to listOf('x', 6, 5, 6, 6, 8),
        "E13"  to listOf(0, 2, 0, 1, 2, 2),   // Open E (Huge Sound: R-5-b7-3-13-9)
        "F13"  to listOf(1, 'x', 1, 2, 3, 'x'), // Shell Voicing (R-b7-3-13). Easy & Clear.
        "Gb13" to listOf(2, 'x', 2, 3, 4, 'x'),
        "G13"  to listOf(3, 'x', 3, 4, 5, 'x'), // Shell Voicing (Standard Jazz G13)
        "Ab13" to listOf(4, 'x', 4, 5, 6, 'x'),
        "A13"  to listOf(5, 'x', 5, 6, 7, 'x'), // or x-0-2-0-2-2
        "Bb13" to listOf('x', 1, 0, 1, 1, 3),   // Bb9 Shape + Pinky
        "B13"  to listOf('x', 2, 1, 2, 2, 4),   // B9 Shape + Pinky3
    )

    val DEFAULT_GUITAR_TUNING = listOf("E2", "A2", "D3", "G3", "B3", "E4")

    /**
     * Finds the guitar tab for a specific chord shape required to sound like the
     * target chord when a capo is applied.
     *
     * @param targetRoot The desired sounding root note (e.g., "C").
     * @param targetQuality The desired sounding quality key (e.g., "maj").
     * @param capoFret The fret number the capo is on (0 for no capo).
     * @return The List<Any> representing the tab for the required SHAPE, or null if not found.
     */
    fun getTabForCapo(targetRoot: String, targetQuality: String, capoFret: Int): List<Any>? {
        if (capoFret <= 0) {
            // No capo, just return the standard tab
            val targetChordName = getChordName(targetRoot, targetQuality)
            return GUITAR_CHORD_TABS[targetChordName]
        }

        val targetRootIndex = ROOT_NOTES.indexOf(targetRoot)
        if (targetRootIndex == -1) return null // Invalid root

        // Calculate the root note of the SHAPE we need to play
        // Go down by `capoFret` semitones from the target root
        val shapeRootIndex = (targetRootIndex - capoFret + ROOT_NOTES.size) % ROOT_NOTES.size
        val shapeRoot = ROOT_NOTES[shapeRootIndex]

        // The quality remains the same
        val shapeQuality = targetQuality

        // Get the name of the chord SHAPE
        val shapeChordName = getChordName(shapeRoot, shapeQuality)

        // Look up the tab for this SHAPE
        // Log.d("ChordUtils", "Capo: $capoFret, Target: $targetRoot$targetQuality, Needs Shape: $shapeChordName")
        return GUITAR_CHORD_TABS[shapeChordName]
    }

    fun tabToNotes(tab: List<Any>, tuning: List<String>, capo: Int): List<String> {
        return tab.mapIndexedNotNull { stringIndex, fret ->
            val fretInt = when (fret) {
                is Number -> fret.toInt()
                is String -> fret.toIntOrNull()
                is Char -> if (fret.lowercaseChar() == 'x') null else fret.toString().toIntOrNull()
                else -> null
            }
            if (fretInt == null || fretInt < 0) return@mapIndexedNotNull null
            val openStringNote = tuning.getOrNull(stringIndex) ?: return@mapIndexedNotNull null
            val openStringMidi = noteNameToMidi(openStringNote) ?: return@mapIndexedNotNull null
            // FIX: Added the 'capo' value to the MIDI calculation for all non-muted strings.
            val noteMidi = openStringMidi + fretInt + capo
            midiToNoteName(noteMidi)
        }
    }

    fun noteNameToMidi(noteName: String): Int? {
        val noteMap = mapOf("C" to 0, "Db" to 1, "D" to 2, "Eb" to 3, "E" to 4, "F" to 5, "Gb" to 6, "G" to 7, "Ab" to 8, "A" to 9, "Bb" to 10, "B" to 11)
        val note = noteName.takeWhile { !it.isDigit() }
        val octave = noteName.takeLastWhile { it.isDigit() }.toIntOrNull() ?: return null
        val noteValue = noteMap[note] ?: return null
        return 12 * (octave + 1) + noteValue
    }

    fun midiToNoteName(midi: Int): String {
        val note = ROOT_NOTES[midi % 12]
        val octave = (midi / 12) - 1
        return "$note$octave"
    }

    val PIANO_KEYS_IN_OCTAVE = listOf(
        "C" to false, "Db" to true, "D" to false, "Eb" to true, "E" to false, "F" to false,
        "Gb" to true, "G" to false, "Ab" to true, "A" to false, "Bb" to true, "B" to false
    )

    fun getChordName(root: String, qualityKey: String): String {
        return root + (CHORD_QUALITIES[qualityKey] ?: "")
    }

    fun parseChordName(chordName: String): Pair<String, String> {
        // Simple parser: Extract root (A-G, optional #/b) and the rest is quality
        if (chordName.isEmpty()) return "" to ""
        
        val rootRegex = Regex("^([A-G][b#]?)")
        val match = rootRegex.find(chordName) ?: return chordName to "" // Fallback
        
        val root = match.value
        val qualityName = chordName.substring(root.length)
        
        // We need to map qualityName back to qualityKey if possible, or just use qualityName
        // PIANO_CHORD_VOICINGS might use the full name or key?
        // calculateChordNotes takes qualityKey (e.g. "m7").
        // CHORD_QUALITIES maps key "m7" -> name "m7".
        // So for "Am7", root="A", qualityName="m7". key is also "m7".
        // For "Cmaj7", root="C", qualityName="maj7". key="maj7".
        // For "C" (Major), root="C", qualityName="". key="maj".
        
        val qualityKey = CHORD_QUALITIES.entries.find { it.value == qualityName }?.key ?: "maj" // Default to maj if empty/unknown
        
        return root to qualityKey
    }

    // Calculates the notes of a chord if it's not in the pre-defined voicings map
    fun calculateChordNotes(root: String, qualityKey: String, baseOctave: Int = 4): List<String> {
        val rootIndex = ROOT_NOTES.indexOf(root)
        if (rootIndex == -1) return emptyList()

        val intervals = CHORD_INTERVALS[qualityKey] ?: return emptyList()

        return intervals.map { interval ->
            val noteIndex = rootIndex + interval
            val octave = baseOctave + (noteIndex / 12)
            val noteName = ROOT_NOTES[noteIndex % 12]
            "$noteName$octave"
        }
    }

    fun detectChordFromNotes(notes: List<String>, preferredRoot: String? = null): ChordMatch? {
        if (notes.size < 2) return null
        val midiValues = notes.mapNotNull { noteNameToMidi(it) }.sorted()
        if (midiValues.size < 2) return null

        // Get the unique notes in the chord, ignoring octaves (e.g., C, E, G)
        val uniqueMidiNotes = midiValues.map { it % 12 }.distinct().sorted()

        // Check preferred root first ---
        if (preferredRoot != null && preferredRoot.isNotBlank()) {
            val rootIndex = ROOT_NOTES.indexOf(preferredRoot)
            if (rootIndex != -1) {
                // Calculate intervals from the *preferred* root
                val intervals = uniqueMidiNotes.map { (it - rootIndex + 12) % 12 }.toSet()
                // Check if these intervals match any known chord
                for ((qualityKey, qualityIntervals) in CHORD_INTERVALS) {
                    if (qualityIntervals.toSet() == intervals) {
                        val qualityName = CHORD_QUALITIES[qualityKey] ?: ""
                        return ChordMatch(preferredRoot, qualityKey, preferredRoot + qualityName)
                    }
                }
            }
        }

        // If no preferred match, fall back to guessing (your original logic)
        for (potentialRootMidi in uniqueMidiNotes) {
            val intervals = uniqueMidiNotes.map { (it - potentialRootMidi + 12) % 12 }.toSet()
            // Check if the intervals match a known chord quality
            for ((qualityKey, qualityIntervals) in CHORD_INTERVALS) {
                if (qualityIntervals.toSet() == intervals) {
                    val rootNoteName = ROOT_NOTES[potentialRootMidi]
                    val qualityName = CHORD_QUALITIES[qualityKey] ?: ""
                    return ChordMatch(rootNoteName, qualityKey, rootNoteName + qualityName)
                }
            }
        }

        // No standard match found
        return null
    }

    /**
     * Extracts all chord names used in the song's text, ordered by their first appearance.
     * All chords in the library that are NOT in the song text are appended alphabetically at the end.
     */
    fun getOrderedChordsInSong(song: Song): List<String> {
        val chordRegex = """\[([^\]]+)\]""".toRegex()
        val orderedInSong = mutableListOf<String>()
        val seen = mutableSetOf<String>()

        song.sections.forEach { section ->
            section.lines.forEach { line ->
                chordRegex.findAll(line.text).forEach { match ->
                    val chordName = match.groupValues[1]
                    if (song.chordDefinitions.containsKey(chordName) && seen.add(chordName)) {
                        orderedInSong.add(chordName)
                    }
                }
            }
        }

        val allLibraryChords = song.chordDefinitions.keys.sorted()
        val remaining = allLibraryChords.filter { !seen.contains(it) }

        return orderedInSong + remaining
    }

    fun removeChordsFromLyrics(text: String): String {
        return text.replace(Regex("\\[[^\\]]+\\]"), "")
    }

    data class DiatonicChord(val numeral: String, val chordName: String)

    fun getDiatonicChords(root: String, scaleType: String): List<DiatonicChord> {
        val rootIndex = ROOT_NOTES.indexOf(root)
        if (rootIndex == -1) return emptyList()

        return if (scaleType == "Major") {
            // Major scale intervals: 0, 2, 4, 5, 7, 9, 11
            val majorIntervals = listOf(0, 2, 4, 5, 7, 9, 11)
            val qualities = listOf("maj", "m", "m", "maj", "maj", "m", "dim")
            val numerals = listOf("I", "ii", "iii", "IV", "V", "vi", "vii°")
            
            majorIntervals.indices.map { i ->
                val noteIndex = (rootIndex + majorIntervals[i]) % 12
                val chordRoot = ROOT_NOTES[noteIndex]
                val chordName = getChordName(chordRoot, qualities[i])
                DiatonicChord(numerals[i], chordName)
            }
        } else {
            // Minor scale intervals: 0, 2, 3, 5, 7, 8, 10
            val minorIntervals = listOf(0, 2, 3, 5, 7, 8, 10)
            val qualities = listOf("m", "dim", "maj", "m", "m", "maj", "maj")
            val numerals = listOf("i", "ii°", "III", "iv", "v", "VI", "VII")
            
            minorIntervals.indices.map { i ->
                val noteIndex = (rootIndex + minorIntervals[i]) % 12
                val chordRoot = ROOT_NOTES[noteIndex]
                val chordName = getChordName(chordRoot, qualities[i])
                DiatonicChord(numerals[i], chordName)
            }
        }
    }
}
